import React, { useEffect, useState, useCallback, useRef } from "react";
import { showError } from "../common/errors";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { Input, Select, Spin, Pagination, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import qs from "query-string";

import { apiBase } from "../utils/apiBase";
import { stringifyFilters } from "../utils/queryParser";
import {
  activityPriorityOptions,
  activityStatusOptions,
} from "../common/activities";
import { recordPathFor } from "../common/recordTypes";
import activitiesActions, {
  fetchUndoneActivityCount,
} from "../actions/activities";

import ActivityListItem from "./ActivityListItem";

import styles from "./ActivityWidgetList.module.scss";

const PAGE_SIZE = 50;

const ActivityWidgetList = ({ onCreate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUserId = useSelector((state) => state.user?.data?.id);
  const { listFilters, listPage } = useSelector(
    (state) => state.activities?.widget || {}
  );
  const { status } = listFilters;

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(listFilters.search || "");
  const debounceRef = useRef(null);

  const fetchActivities = useCallback(() => {
    if (!currentUserId) return;

    const filters = { user_id: currentUserId, status };
    if (listFilters.priority) {
      filters.priority = listFilters.priority;
    }

    const query = {
      filters: stringifyFilters(filters),
      limit: PAGE_SIZE,
      page: listPage,
    };
    if (listFilters.search) {
      query.search = listFilters.search;
    }

    const queryString = qs.stringify(query);

    setLoading(true);
    apiBase
      .get(`/activities?${queryString}`)
      .then((res) => {
        setData(res.data?.activities || []);
        setTotal(res.data?.total ?? res.data?.count ?? 0);
      })
      .catch((err) => {
        setData([]);
        setTotal(0);
        showError(err, "Failed to load activities");
      })
      .finally(() => setLoading(false));
  }, [listFilters, status, listPage, currentUserId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleSearchChange = (e) => {
    const value = e.target.value;

    setSearchInput(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const trimmed = value.trim();
      const next = trimmed.length >= 3 ? trimmed : "";

      dispatch(activitiesActions.setListFilters({ search: next }));
    }, 300);
  };

  const handleToggleDone = (activity, done) => {
    apiBase
      .patch(`/activities/${activity.id}`, { done })
      .then(() => {
        fetchActivities();
        dispatch(fetchUndoneActivityCount());
      })
      .catch((err) => showError(err, "Failed to update activity"));
  };

  const handleItemClick = (item) => {
    dispatch(activitiesActions.setSelectedActivityId(item.id));

    const path = recordPathFor(item.record_type, item.record_id, item.record);
    if (path) {
      navigate(path);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.filtersRow}>
          <Input
            placeholder="Search"
            value={searchInput}
            onChange={handleSearchChange}
            size="small"
            allowClear
            className={styles.searchInput}
          />
          <Select
            size="small"
            options={activityPriorityOptions}
            allowClear
            placeholder="Priority"
            className={styles.priorityFilter}
            value={listFilters.priority}
            onChange={(value) =>
              dispatch(
                activitiesActions.setListFilters({ priority: value ?? null })
              )
            }
          />
          <Select
            size="small"
            value={status}
            onChange={(value) =>
              dispatch(activitiesActions.setListFilters({ status: value }))
            }
            options={activityStatusOptions}
            className={styles.statusFilter}
          />
          <Button
            icon={<PlusOutlined />}
            onClick={onCreate}
            size="small"
            style={{ marginLeft: "auto" }}
          >
            New
          </Button>
        </div>
      </div>

      <div className={styles.list}>
        {loading ? (
          <div className={styles.loading}>
            <Spin />
          </div>
        ) : data.length === 0 ? (
          <div className={styles.empty}>No activities</div>
        ) : (
          data.map((item) => (
            <ActivityListItem
              key={item.id}
              activity={item}
              onClick={handleItemClick}
              onToggleDone={handleToggleDone}
            />
          ))
        )}
      </div>

      <div className={styles.footer}>
        <Link
          to={`/activities?${qs.stringify({
            limit: PAGE_SIZE,
            page: 1,
            filters: stringifyFilters({
              status,
              ...(listFilters.priority
                ? { priority: listFilters.priority }
                : {}),
            }),
          })}`}
          className={styles.viewAll}
        >
          View all
        </Link>
        {total > PAGE_SIZE && (
          <Pagination
            current={listPage}
            total={total}
            pageSize={PAGE_SIZE}
            size="small"
            showSizeChanger={false}
            simple
            onChange={(page) => dispatch(activitiesActions.setListPage(page))}
          />
        )}
      </div>
    </div>
  );
};

export default ActivityWidgetList;
